import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import isPlainObject from 'lodash/isPlainObject';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  useNotify,
} from 'core/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import DocumentOptionsStep from 'views/EMR/components/FillableForm/components/DocumentOptionsStep';
import EmptyWorkflowStepBar from 'views/EMR/components/FillableForm/components/EmptyWorkflowStepBar';
import FieldWizardBar from 'views/EMR/components/FillableForm/components/FieldWizardBar';
import PdfFormViewer from 'views/EMR/components/FillableForm/components/PdfFormViewer';
import PreparationSteps, {
  PreparationCompleteData,
} from 'views/EMR/components/FillableForm/components/PreparationSteps';
import Checkbox from 'views/EMR/components/FillableForm/fields/Checkbox';
import CurrencyTextField from 'views/EMR/components/FillableForm/fields/CurrencyTextField';
import DateField from 'views/EMR/components/FillableForm/fields/Date';
import LongText from 'views/EMR/components/FillableForm/fields/LongText';
import NumberInput from 'views/EMR/components/FillableForm/fields/NumberInput';
import Phone from 'views/EMR/components/FillableForm/fields/Phone';
import RadioGroupField from 'views/EMR/components/FillableForm/fields/Radio';
import CheckboxGroupField from 'views/EMR/components/FillableForm/fields/CheckboxGroup';
import ShortText from 'views/EMR/components/FillableForm/fields/ShortText';
import SignatureInput from 'views/EMR/components/FillableForm/fields/SignatureInput';
import SignatureTimestampField from 'views/EMR/components/FillableForm/fields/SignatureTimestamp';
import TaxpayerNumber from 'views/EMR/components/FillableForm/fields/TaxpayerNumber';
import USAddress from 'views/EMR/components/FillableForm/fields/USAddress';
import {
  FieldComponentProps,
  FieldType,
  FillableFormField,
  FillableFormProps,
  FormFieldsData,
  WizardStep,
} from 'views/EMR/components/FillableForm/types';
import {
  normalizeFieldValues,
  normalizeFormData,
} from 'views/EMR/components/FillableForm/utils/normalizeFieldValues';
import validateFormFieldValue, {
  getFormFieldValidationMessage,
} from 'views/EMR/components/FillableForm/utils/fieldValidation';
import {
  applySharedSignatureToFields,
  getWizardStepAfterLoad,
  mergeSharedPrepValues,
  resolveGlobalPrepDefaults,
  shouldShowPreparationStep,
} from 'views/EMR/components/FillableForm/utils/signaturePrep';
import {
  isOrderOrReferralCategory,
} from 'views/EMR/components/FormWorkflow/utils';

const animationClass = 'transition-all duration-300 ease-in-out transform';

const preparationFields = [
  {
    title: 'Draw or type your signature',
    name: 'signature' as const,
    description: 'Your signature will appear on all documents where you need to sign.',
    placeholder: 'Ex. John Doe',
  },
  {
    title: 'Initials',
    name: 'initial' as const,
    description: 'Your initials will appear on all documents where you need to initial.',
    placeholder: 'Ex. JD',
  },
];

function getInitialStep(
  hasSignatureField: boolean,
  hasInitialField: boolean,
  sharedPrepValues: PreparationCompleteData | null | undefined,
): WizardStep {
  return getWizardStepAfterLoad(hasSignatureField, hasInitialField, sharedPrepValues);
}

function hasFieldByType(fields: FillableFormField[], fieldName: FieldType): boolean {
  return some(fields, { type: fieldName });
}

const componentMap: Record<Partial<FieldType>, FC<FieldComponentProps>> = {
  radioGroup: RadioGroupField,
  checkboxGroup: CheckboxGroupField,
  checkbox: Checkbox,
  fullName: ShortText,
  phone: Phone,
  email: ShortText,
  ssn: TaxpayerNumber,
  ein: TaxpayerNumber,
  usAddress: USAddress,
  longText: LongText,
  shortText: ShortText,
  signature: SignatureInput,
  initial: SignatureInput,
  date: DateField,
  signatureDate: DateField,
  signatureTimestamp: SignatureTimestampField,
  number: NumberInput,
  integer: NumberInput,
  dollar: CurrencyTextField,
  percent: NumberInput,
};

export default function FillableForm({
  session,
  category,
  onClose,
  workflow,
  isSubmitting = false,
  sharedPrepValues = null,
  onSharedPrepComplete,
}: FillableFormProps) {
  const notify = useNotify();
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldsRefs = useRef<Record<string, HTMLElement>>({});
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [wizardStep, setWizardStep] = useState<WizardStep>(() => getInitialStep(
    hasFieldByType(session.fields, 'signature'),
    hasFieldByType(session.fields, 'initial'),
    sharedPrepValues,
  ));
  const [includeVisitNote, setIncludeVisitNote] = useState(false);

  const formProps = useForm<FormFieldsData>({
    mode: 'onChange',
    defaultValues: {
      signature: '',
      signatureDraw: '',
      signatureTyped: '',
      initial: '',
      initialDraw: '',
      initialTyped: '',
    },
  });

  const {
    control,
    watch,
    reset,
    handleSubmit,
    clearErrors,
    setError: setFormError,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = formProps;

  const hasSignatureField = useMemo(
    () => hasFieldByType(session.fields, 'signature'),
    [session.fields],
  );
  const hasInitialField = useMemo(
    () => hasFieldByType(session.fields, 'initial'),
    [session.fields],
  );
  const hasPrepFields = hasSignatureField || hasInitialField;
  const shouldShowPreparation = shouldShowPreparationStep(
    hasSignatureField,
    hasInitialField,
    sharedPrepValues,
  );
  const globalPrepDefaults = useMemo(
    () => resolveGlobalPrepDefaults(sharedPrepValues),
    [sharedPrepValues],
  );

  const formValues = watch();

  const fields = useMemo(
    () => (isDocumentLoaded ? session.fields : []),
    [isDocumentLoaded, session.fields],
  );

  useEffect(() => {
    if (!isDocumentLoaded) {
      return;
    }

    const fieldDefaults = fields.map((field) => {
      if (field.type === 'usAddress') {
        return {
          ...field,
          value: {
            street: '',
            unit: '',
            city: '',
            state: '',
            zipCode: '',
          },
        };
      }
      if (field.type === 'checkbox') {
        return { ...field, value: false };
      }
      if (field.type === 'checkboxGroup') {
        return { ...field, value: [] };
      }
      if (field.type === 'signature' && !isEmptyString(globalPrepDefaults.signature)) {
        return { ...field, value: globalPrepDefaults.signature };
      }
      if (field.type === 'initial' && !isEmptyString(globalPrepDefaults.initial)) {
        return { ...field, value: globalPrepDefaults.initial };
      }
      return { ...field, value: field.value ?? '' };
    });

    reset({
      ...globalPrepDefaults,
      ...(fields.length > 0 ? keyBy(fieldDefaults, 'id') : {}),
    });

    setWizardStep(getInitialStep(hasSignatureField, hasInitialField, sharedPrepValues));
    setCurrentFieldIndex(0);
    setIncludeVisitNote(false);
  }, [fields, globalPrepDefaults, hasPrepFields, isDocumentLoaded, reset, sharedPrepValues]);

  const filteredPreparationFields = useMemo(
    () => preparationFields.filter((field) => {
      if (field.name === 'signature') {
        return hasSignatureField && isEmptyString(sharedPrepValues?.signature);
      }
      if (field.name === 'initial') {
        return hasInitialField && isEmptyString(sharedPrepValues?.initial);
      }
      return true;
    }),
    [hasSignatureField, hasInitialField, sharedPrepValues],
  );

  const isFormFillable = wizardStep === 'filling';

  const showTopBar = wizardStep === 'filling';

  const showDocumentOptionsStep = isOrderOrReferralCategory(category);
  const totalFillingSteps = fields.length + (showDocumentOptionsStep ? 1 : 0);
  const isOnFieldStep = wizardStep === 'filling' && currentFieldIndex < fields.length;
  const isOnDocumentOptionsStep = wizardStep === 'filling'
    && showDocumentOptionsStep
    && currentFieldIndex === fields.length;
  const isOnEmptyWorkflowStep = wizardStep === 'filling'
    && fields.length === 0
    && !showDocumentOptionsStep;
  const emptyWorkflowContinueLabel = workflow.isLastForm
    ? `Complete ${category.name}`
    : 'Continue to next form';

  const currentField = useMemo(
    () => (isOnFieldStep ? fields[currentFieldIndex] : undefined) as FillableFormField | undefined,
    [currentFieldIndex, fields, isOnFieldStep],
  );

  const Component = useMemo(
    () => componentMap[currentField?.type as FieldType],
    [currentField],
  );

  function updateContainerWidth() {
    if (!isNil(containerRef.current)) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }

  useEffect(() => {
    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  useEffect(() => {
    if (isOnFieldStep) {
      const fieldId = fields[currentFieldIndex]?.id ?? '';
      const currentRef = fieldsRefs.current[fieldId];
      if (currentRef && isFormFillable) {
        currentRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentFieldIndex, isFormFillable, fields, isOnFieldStep]);

  const focusFieldById = useCallback((fieldId?: string | null) => {
    if (!isNil(fieldId)) {
      const nextFieldIndex = fields.findIndex((field) => field.id === fieldId);

      if (nextFieldIndex >= 0) {
        setCurrentFieldIndex(nextFieldIndex);
      }
    }
  }, [fields]);

  const syncFieldValidationState = useCallback((
    field: FillableFormField,
    fieldData?: FormFieldsData[string],
  ) => {
    const isValid = validateFormFieldValue(
      fieldData ?? getValues(field.id ?? ''),
      field,
    );

    if (isValid) {
      clearErrors(field.id ?? '');
      return true;
    }

    setFormError(field.id ?? '', {
      type: 'manual',
      message: getFormFieldValidationMessage(field),
    });
    return false;
  }, [clearErrors, getValues, setFormError]);

  const validateAllFields = useCallback((formData: FormFieldsData) => fields.reduce<string[]>(
    (invalidFieldIds, field) => {
      if (syncFieldValidationState(field, formData[field.id ?? ''])) {
        return invalidFieldIds;
      }

      return [...invalidFieldIds, field.id ?? ''];
    },
    [],
  ), [fields, syncFieldValidationState]);

  const handleNextButton = useCallback(() => {
    setCurrentFieldIndex((prev) => (prev < fields.length ? prev + 1 : prev));
  }, [fields.length]);

  const handleFillingPrev = useCallback(() => {
    let nextIndex = currentFieldIndex;

    if (isOnDocumentOptionsStep && fields.length > 0) {
      nextIndex = fields.length - 1;
    } else if (currentFieldIndex > 0) {
      nextIndex = currentFieldIndex - 1;
    }

    setCurrentFieldIndex(nextIndex);
  }, [currentFieldIndex, fields.length, isOnDocumentOptionsStep]);

  const handlePreparationStart = useCallback((data: PreparationCompleteData) => {
    const merged = mergeSharedPrepValues(sharedPrepValues, data);
    setValue('signature', merged.signature);
    setValue('initial', merged.initial);
    applySharedSignatureToFields(fields, merged, setValue, getValues);
    onSharedPrepComplete?.(data);
    setWizardStep('filling');
  }, [fields, getValues, onSharedPrepComplete, setValue, sharedPrepValues]);

  const handlePdfFieldValueChange = useCallback((fieldId: string, value: boolean | string | string[]) => {
    const previousValue = getValues(fieldId);
    const fieldValue: Record<string, unknown> = isPlainObject(previousValue)
      ? previousValue as Record<string, unknown>
      : {};

    const nextFieldData = {
      ...fieldValue,
      value,
    };

    setValue(
      fieldId,
      nextFieldData,
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      },
    );

    const matchedField = fields.find((field) => field.id === fieldId);

    if (matchedField) {
      syncFieldValidationState(matchedField, nextFieldData);
    }
  }, [fields, getValues, setValue, syncFieldValidationState]);

  const handlePdfFieldActivate = useCallback((fieldId: string) => {
    const nextFieldIndex = fields.findIndex((field) => field.id === fieldId);

    if (nextFieldIndex >= 0) {
      setCurrentFieldIndex(nextFieldIndex);
    }
  }, [fields]);

  const validateCurrentField = useCallback(() => {
    if (!currentField?.id) {
      return true;
    }

    return syncFieldValidationState(currentField, getValues(currentField.id));
  }, [currentField, getValues, syncFieldValidationState]);

  const completeWorkflowFields = useCallback(async (shouldIncludeVisitNote = false) => {
    const normalized = normalizeFormData(getValues(), fields);

    try {
      await workflow.onComplete({
        interactiveFieldValues: normalizeFieldValues(normalized),
        includeVisitNote: shouldIncludeVisitNote,
        formId: session.formId,
      });
    } catch (saveError) {
      const message = saveError instanceof Error
        ? saveError.message
        : 'Failed to complete form workflow';
      notify({ message, variant: 'error' });
    }
  }, [fields, getValues, notify, session.formId, workflow]);

  const handleContinue = useCallback(() => {
    if (validateCurrentField()) {
      handleNextButton();
      return;
    }

    focusFieldById(currentField?.id);
  }, [currentField?.id, focusFieldById, handleNextButton, validateCurrentField]);

  const handleSubmitAttempt = useCallback(async (formData: FormFieldsData) => {
    const invalidFieldIds = validateAllFields(formData);

    if (invalidFieldIds.length > 0) {
      focusFieldById(invalidFieldIds[0]);
      return;
    }

    if (showDocumentOptionsStep) {
      setCurrentFieldIndex(fields.length);
      return;
    }

    await completeWorkflowFields();
  }, [
    completeWorkflowFields,
    fields.length,
    focusFieldById,
    showDocumentOptionsStep,
    validateAllFields,
  ]);

  const handleInvalidSubmit = useCallback(() => {
    const invalidFieldIds = validateAllFields(getValues());

    if (invalidFieldIds.length > 0) {
      focusFieldById(invalidFieldIds[0]);
    }
  }, [focusFieldById, getValues, validateAllFields]);

  const handleDocumentOptionsConfirm = useCallback(async () => {
    const invalidFieldIds = validateAllFields(getValues());

    if (invalidFieldIds.length > 0) {
      focusFieldById(invalidFieldIds[0]);
      return;
    }

    await completeWorkflowFields(includeVisitNote);
  }, [completeWorkflowFields, focusFieldById, getValues, includeVisitNote, validateAllFields]);

  const handleEmptyWorkflowContinue = useCallback(async () => {
    await completeWorkflowFields();
  }, [completeWorkflowFields]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {!isEmptyString(error) && (
        <div className="flex flex-1 justify-center items-center p-8">
          <p className="text-red-500 text-center">{error}</p>
        </div>
      )}
      {!isDocumentLoaded && isEmptyString(error) && (
        <div className="absolute left-0 right-0 bg-white h-full z-10 flex items-center justify-center">
          <Spinner className="items-center h-full" />
        </div>
      )}
      <FormProvider {...formProps}>
        <div
          className={clsx(
            'flex-shrink-0 flex justify-center w-full bg-white shadow-md z-[999]',
            { 'opacity-100 py-4': showTopBar },
            { 'h-0 overflow-hidden opacity-0 py-0': !showTopBar },
            animationClass,
          )}
        >
          {isOnFieldStep && currentField && Component && (
              <FieldWizardBar
                currentField={currentField}
                currentFieldIndex={currentFieldIndex}
                totalSteps={totalFillingSteps}
                control={control}
                Component={Component}
                validate={validateFormFieldValue}
                handleSubmit={handleSubmit}
                onSubmit={handleSubmitAttempt}
                onInvalidSubmit={handleInvalidSubmit}
                onFieldChange={(nextValue) => {
                  syncFieldValidationState(currentField, nextValue);
                }}
                onContinue={handleContinue}
                onTrigger={trigger}
                onPrev={handleFillingPrev}
                onNext={handleNextButton}
                isLastFieldStep={currentFieldIndex === fields.length - 1}
                isSaving={isSubmitting}
              />
          )}
          {isOnDocumentOptionsStep && (
            <DocumentOptionsStep
              currentStep={currentFieldIndex + 1}
              totalSteps={totalFillingSteps}
              category={category}
              includeVisitNote={includeVisitNote}
              onIncludeVisitNoteChange={setIncludeVisitNote}
              onConfirm={handleDocumentOptionsConfirm}
              onPrev={handleFillingPrev}
              isSaving={isSubmitting}
            />
          )}
          {isOnEmptyWorkflowStep && (
            <EmptyWorkflowStepBar
              continueLabel={emptyWorkflowContinueLabel}
              onContinue={handleEmptyWorkflowContinue}
              isSaving={isSubmitting}
            />
          )}
        </div>
        <div className="overflow-y-auto flex-1 min-h-0">
          <div className={clsx('bg-white mb-4', { hidden: !isEmptyString(error) })}>
            {shouldShowPreparation && wizardStep === 'preparation' && (
              <PreparationSteps
                fields={filteredPreparationFields}
                onStart={handlePreparationStart}
              />
            )}
            <div className={clsx('m-auto max-w-[870px] shadow-md p-4')} ref={containerRef}>
              <PdfFormViewer
                session={session}
                containerWidth={containerWidth}
                formValues={formValues}
                currentFieldId={currentField?.id ?? undefined}
                invalidFieldIds={Object.keys(errors)}
                isFormFillable={isFormFillable}
                fieldsRefs={fieldsRefs}
                onFieldActivate={handlePdfFieldActivate}
                onFieldValueChange={handlePdfFieldValueChange}
                onLoadSuccess={() => { setIsDocumentLoaded(true); }}
                onError={(message) => { setError(message); }}
              />
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
}
